import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import socketService from '../services/socket';
import '../styles/JoinRequestPanel.css';

interface JoinRequest {
  id: number;
  room_id: number;
  user_id: number;
  username: string;
  status: string;
  created_at: string;
}

interface JoinRequestPanelProps {
  roomId: number;
  isInActiveGame?: boolean;
}

const JoinRequestPanel: React.FC<JoinRequestPanelProps> = ({ roomId, isInActiveGame }) => {
  const { user } = useAuth();
  const [requests, setRequests] = useState<JoinRequest[]>([]);
  const [notifications, setNotifications] = useState<string[]>([]);
  const [error, setError] = useState('');

  const fetchPendingRequests = async () => {
    try {
      const data = await apiService.get(`/api/room-browser/pending-requests/${roomId}`);
      setRequests(data.requests || []);
    } catch (err: any) {
      console.error('Error fetching pending requests:', err);
    }
  };

  const handleNewRequest = (data: any) => {
    if (data.roomId === roomId) {
      setRequests(prev => [...prev, {
        id: data.requestId,
        room_id: data.roomId,
        user_id: data.requesterUserId,
        username: data.requesterUsername,
        status: 'pending',
        created_at: data.timestamp
      }]);
    }
  };

  const handleRequestUpdate = (data: any) => {
    if (data.roomId === roomId) {
      // Don't show notification if current user is the requester (they get their own notification)
      if (data.requesterUserId === user?.id) {
        console.log('⏭️ Skipping JoinRequestPanel notification - current user is the requester');
        return;
      }

      // Remove request from list
      setRequests(prev => prev.filter(req => req.id !== data.requestId));

      // Show notification to room members
      const message = `${data.responderUsername} ${data.action} ${data.requesterUsername}'s join request`;
      setNotifications(prev => [...prev, message]);

      // Auto-remove notification after 5 seconds
      setTimeout(() => {
        setNotifications(prev => prev.filter(msg => msg !== message));
      }, 5000);
    }
  };

  useEffect(() => {
    if (!roomId) return;

    // Fetch pending requests
    fetchPendingRequests();

    // Listen for new join requests
    socketService.on('new_join_request', handleNewRequest);
    socketService.on('join_request_update', handleRequestUpdate);

    return () => {
      socketService.off('new_join_request', handleNewRequest);
      socketService.off('join_request_update', handleRequestUpdate);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId]);

  const handleRespond = async (requestId: number, action: 'accept' | 'reject') => {
    try {
      setError('');
      await apiService.post('/api/room-browser/respond-request', { requestId, action });

      // Emit socket event for real-time update
      socketService.getSocket()?.emit('join_request_responded', {
        roomId,
        requestId,
        action,
        responderUsername: user?.username
      });

      // Remove from local state
      setRequests(prev => prev.filter(req => req.id !== requestId));
    } catch (err: any) {
      setError(err.response?.data?.error || `Failed to ${action} request`);
    }
  };

  const getTimeAgo = (timestamp: string) => {
    const now = new Date().getTime();
    const then = new Date(timestamp).getTime();
    const seconds = Math.floor((now - then) / 1000);

    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)} hr ago`;
    return `${Math.floor(seconds / 86400)} days ago`;
  };

  // Don't show during active gameplay
  if (isInActiveGame || (requests.length === 0 && notifications.length === 0)) {
    return null;
  }

  return (
    <div className="join-request-panel">
      <h4 className="panel-title">📩 Join Requests</h4>

      {error && <div className="error-message">{error}</div>}

      {/* Notification messages */}
      {notifications.map((msg, index) => (
        <div key={index} className="notification-banner">
          {msg}
        </div>
      ))}

      {/* Pending requests */}
      {requests.length > 0 && (
        <div className="requests-list">
          {requests.map(request => (
            <div key={request.id} className="request-item">
              <div className="request-info">
                <span className="requester-name">{request.username}</span>
                <span className="request-time">{getTimeAgo(request.created_at)}</span>
              </div>
              <div className="request-actions">
                <button
                  className="accept-btn"
                  onClick={() => handleRespond(request.id, 'accept')}
                >
                  ✓ Accept
                </button>
                <button
                  className="reject-btn"
                  onClick={() => handleRespond(request.id, 'reject')}
                >
                  ✗ Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default JoinRequestPanel;
